import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { Subscription } from 'rxjs';
import { SessionService } from 'src/app/core/session/session.service';

/**
 * Sign-in page. It only offers the button: the session is handled by SessionService and the route guards, so this
 * page never processes tokens (it used to, and bounced against the guards).
 */
@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, OnDestroy {
  private readonly auth = inject(AuthService);
  private readonly session = inject(SessionService);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);

  private sessionSub?: Subscription;
  private readonly defaultPath = '/welcome';

  ngOnInit(): void {
    // Someone with a live (confirmed) session who lands here (bookmark, back button) goes on to the app.
    this.sessionSub = this.session.ensureSession().subscribe((state) => {
      if (state === 'valid') {
        this.router.navigateByUrl(this.returnUrl, { replaceUrl: true });
      }
    });
  }

  login() {
    // Ask for credentials explicitly only when Auth0 still holds a session we rejected (unverified email): a silent
    // sign-in would bring the same user straight back. Otherwise Auth0 reuses its own session (SSO).
    this.auth.loginWithRedirect({
      appState: { target: this.returnUrl },
      ...(this.session.lastState === 'unverified' ? { authorizationParams: { prompt: 'login' } } : {}),
    });
  }

  ngOnDestroy(): void {
    this.sessionSub?.unsubscribe();
  }

  /** Page the user was heading to when the guard sent them here (app-internal paths only). */
  private get returnUrl(): string {
    const url = this.activatedRoute.snapshot.queryParams['returnUrl'];
    return typeof url === 'string' && url.startsWith('/') && !url.startsWith('//') ? url : this.defaultPath;
  }
}
